import { useMemo } from 'react';
import type { Resource, TagFilter } from '../types';
import { isFilters } from '../types';
import { parseTag } from '../utils';
import { FilterMenu } from './filter-menu';
import { FilterMultiSelect } from './filter-multi-select';
import { FilterSelect } from './filter-select';

/** 静态配置项与动态数据的结合 */
type CombineFilter = TagFilter & Partial<Resource>;

interface DroplistProps {
  inputValue?: string;
  onInputChange?: (value: string) => void;
  tags: string[];
  filters: TagFilter[];
  resource?: Resource[];
  values?: Record<string, any>;
  onAddTag?: (tag: string, key: string, value: any) => void;
  onCloseDropdown?: () => void;
}

export function Droplist({
  inputValue,
  onInputChange,
  tags,
  filters,
  resource,
  values,
  onAddTag,
  onCloseDropdown,
}: DroplistProps) {
  const resourceMap = useMemo(() => {
    const map = new Map<string, Resource>();
    resource?.forEach((res) => {
      map.set(res.key, res);
    });
    return map;
  }, [resource]);

  /**
   * 检查当前输入内容是否包含 label
   * 如果是: 返回当前的 filter
   * 如果不是: 返回undefined
   */
  const matchFilterByInput: CombineFilter | undefined = useMemo(() => {
    const matchInputLabel = parseTag(inputValue || '')?.label;
    if (matchInputLabel) {
      const filter = filters.find((f) => isFilters(f) && f.label === matchInputLabel);
      if (filter) {
        const res = resourceMap.get(filter.key);
        return {
          ...filter,
          ...res,
        };
      }
    }
    return undefined;
  }, [inputValue, filters, resourceMap]);

  // 如果输入值匹配到了某个 filter，显示对应的组件
  if (matchFilterByInput) {
    const { type } = matchFilterByInput;

    if (type === 'input') {
      // input 类型不显示下拉选项，用户可以直接输入
      return null;
    } else if (type === 'select') {
      return (
        <FilterSelect
          filter={matchFilterByInput}
          inputValue={inputValue}
          onAddTag={onAddTag}
          onCloseDropdown={onCloseDropdown}
        />
      );
    } else if (type === 'multiple-select') {
      return (
        <FilterMultiSelect
          filter={matchFilterByInput}
          inputValue={inputValue}
          values={values}
          onAddTag={onAddTag}
          onCloseDropdown={onCloseDropdown}
        />
      );
    }
  }

  // 默认显示筛选菜单
  return <FilterMenu tags={tags} filters={filters} onInputChange={onInputChange} />;
}
